import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { lawyerInfoService } from '../services/lawyerInfoService';
import { RepresentativeInfo, LawyerFormData } from '../types/lawyer';
import './LawyerInfoModal.css';

interface LawyerInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  existingData?: RepresentativeInfo;
  existingIndustry?: 'legal' | 'real_estate'; // Industry is at top level, not in representative_info
}

// Predefined country options - deduplicated and sorted
const COUNTRY_OPTIONS = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Angola',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahrain',
  'Bangladesh',
  'Belarus',
  'Belgium',
  'Benin',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Comoros',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Egypt',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Ethiopia',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Guinea',
  'Guinea-Bissau',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Ivory Coast',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kosovo',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malaysia',
  'Mali',
  'Malta',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Moldova',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Niger',
  'Nigeria',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Republic of the Congo',
  'Romania',
  'Rwanda',
  'São Tomé and Príncipe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovenia',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Sweden',
  'Switzerland',
  'Syria',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Togo',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uzbekistan',
  'Vietnam',
  'Western Sahara',
  'Yemen',
  'Zambia',
  'Zimbabwe'
].sort();

const LawyerInfoModal: React.FC<LawyerInfoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  existingData,
  existingIndustry
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [formData, setFormData] = useState<LawyerFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '1',
    phoneNumber: '',
    lawFirm: '',
    supervisingLawyerName: '',
    supervisingLawyerMembershipID: '',
    address: {
      streetNumber: '',
      streetName: '',
      aptNumber: '',
      city: '',
      province: '',
      country: '',
      postalCode: ''
    },
    faxCountryCode: '1',
    faxNumber: '',
    industry: 'legal'
  });

  useEffect(() => {
    // Load industry from user profile if available
    const loadIndustry = async () => {
      if (user?.id) {
        try {
          const { data } = await lawyerInfoService.getLawyerInfo(user.id);
          if (data?.industry) {
            setFormData(prev => ({ ...prev, industry: data.industry as 'legal' | 'real_estate' }));
          }
        } catch (error) {
          console.error('Error loading industry:', error);
        }
      }
    };
    loadIndustry();
  }, [user?.id]);

  useEffect(() => {
    if (existingData) {
      const countryValue = existingData.address?.country || '';
      setFormData(prev => ({
        ...prev,
        firstName: existingData.firstName || '',
        lastName: existingData.lastName || '',
        email: existingData.email || '',
        phoneCountryCode: existingData.phoneCountryCode || '1',
        phoneNumber: existingData.phoneNumber || '',
        lawFirm: existingData.lawFirm || '',
        supervisingLawyerName: existingData.supervisingLawyerName || '',
        supervisingLawyerMembershipID: existingData.supervisingLawyerMembershipID || '',
        address: {
          streetNumber: existingData.address?.streetNumber || '',
          streetName: existingData.address?.streetName || '',
          aptNumber: existingData.address?.aptNumber || '',
          city: existingData.address?.city || '',
          province: existingData.address?.province || '',
          country: countryValue,
          postalCode: existingData.address?.postalCode || ''
        },
        faxCountryCode: existingData.faxCountryCode || '1',
        faxNumber: existingData.faxNumber || '',
        industry: existingIndustry || 'legal' // Use top-level industry field, default to 'legal'
      }));
      // Sync countrySearchTerm with formData.address.country
      setCountrySearchTerm(countryValue);
    } else {
      // Initialize countrySearchTerm as empty when no existing data
      setCountrySearchTerm('');
    }
  }, [existingData]);

  // Sync countrySearchTerm with formData.address.country when formData changes
  // This keeps the input field in sync with the actual form data
  // Use a ref to track if user is actively typing to avoid overriding their input
  const isTypingCountryRef = useRef(false);
  
  useEffect(() => {
    // Only sync if user is not actively typing and values don't match
    if (!isTypingCountryRef.current && formData.address.country && formData.address.country !== countrySearchTerm) {
      setCountrySearchTerm(formData.address.country);
    }
  }, [formData.address.country]);

  // Filter countries based on search term
  const filteredCountries = COUNTRY_OPTIONS.filter(country =>
    country.toLowerCase().includes(countrySearchTerm.toLowerCase())
  ).sort((a, b) => {
    const searchTerm = countrySearchTerm.toLowerCase();
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    
    // Check if country starts with search term
    const aStartsWith = aLower.startsWith(searchTerm);
    const bStartsWith = bLower.startsWith(searchTerm);
    
    // If one starts with search term and the other doesn't, prioritize the one that starts with it
    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;
    
    // If both start with search term or neither does, sort alphabetically
    return aLower.localeCompare(bLower);
  });

  const handleCountrySelect = (country: string) => {
    isTypingCountryRef.current = false; // User selected, not typing
    updateAddress('country', country);
    setCountrySearchTerm(country);
    setShowCountryDropdown(false);
  };

  const handleCountryInputChange = (value: string) => {
    isTypingCountryRef.current = true;
    setCountrySearchTerm(value);
    updateAddress('country', value);
    setShowCountryDropdown(true);
    // Reset typing flag after a delay
    setTimeout(() => {
      isTypingCountryRef.current = false;
    }, 500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      console.error('No user ID available');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await lawyerInfoService.saveLawyerInfo(user.id, formData);
      
      if (error) {
        console.error('Error saving lawyer info:', error);
        alert('Error saving lawyer information. Please try again.');
        return;
      }

      onSave();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      alert('Error saving lawyer information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateAddress = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="lawyer-info-modal">
        <div className="modal-header">
          <h2>Representative Information</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-sections">
            {/* Industry Selection */}
            <div className="form-section">
              <h3>Industry</h3>
              <div className="form-group">
                <label>Select Your Industry *</label>
                <select
                  value={formData.industry || 'legal'}
                  onChange={(e) => setFormData({...formData, industry: e.target.value as 'legal' | 'real_estate'})}
                  required
                >
                  <option value="legal">Legal</option>
                  <option value="real_estate">Real Estate</option>
                </select>
                <p className="form-hint">
                  This determines which intake forms will be available to your clients. Legal professionals see immigration and legal forms, while Real Estate professionals see property-related forms.
                </p>
              </div>
            </div>

            {/* Personal Information */}
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="form-section">
              <h3>Professional Information</h3>
              <div className="form-group">
                <label>Firm Name *</label>
                <input
                  type="text"
                  value={formData.lawFirm}
                  onChange={(e) => setFormData({...formData, lawFirm: e.target.value})}
                  required
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="form-section">
              <h3>Address Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Street Number *</label>
                  <input
                    type="text"
                    value={formData.address.streetNumber}
                    onChange={(e) => updateAddress('streetNumber', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Street Name *</label>
                  <input
                    type="text"
                    value={formData.address.streetName}
                    onChange={(e) => updateAddress('streetName', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Province *</label>
                  <input
                    type="text"
                    value={formData.address.province}
                    onChange={(e) => updateAddress('province', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country *</label>
                  <div className="country-combobox">
                    <input
                      type="text"
                      value={countrySearchTerm}
                      onChange={(e) => handleCountryInputChange(e.target.value)}
                      onFocus={() => setShowCountryDropdown(true)}
                      placeholder="Type or select a country (e.g., Canada, United States)"
                      required
                    />
                    {showCountryDropdown && (
                      <div className="country-dropdown">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <div
                              key={country}
                              className="country-option"
                              onClick={() => handleCountrySelect(country)}
                            >
                              {country}
                            </div>
                          ))
                        ) : (
                          <div className="country-option no-results">
                            No countries found. You can type your own.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Postal Code *</label>
                  <input
                    type="text"
                    value={formData.address.postalCode}
                    onChange={(e) => updateAddress('postalCode', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Country Code *</label>
                  <select
                    value={formData.phoneCountryCode}
                    onChange={(e) => setFormData({...formData, phoneCountryCode: e.target.value})}
                    required
                  >
                    <option value="1">+1 (US/Canada)</option>
                    <option value="44">+44 (UK)</option>
                    <option value="91">+91 (India)</option>
                    <option value="61">+61 (Australia)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Fax Country Code</label>
                  <select
                    value={formData.faxCountryCode}
                    onChange={(e) => setFormData({...formData, faxCountryCode: e.target.value})}
                  >
                    <option value="1">+1 (US/Canada)</option>
                    <option value="44">+44 (UK)</option>
                    <option value="91">+91 (India)</option>
                    <option value="61">+61 (Australia)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Fax Number</label>
                  <input
                    type="tel"
                    value={formData.faxNumber}
                    onChange={(e) => setFormData({...formData, faxNumber: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Information'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LawyerInfoModal; 